import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { ProjectCard } from "@/components/ProjectCard";
import { ConsultButton } from "@/components/CtaButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { PROCESS_STEPS } from "@/data/content";
import { api } from "@/lib/api";

export const ProcessPreview = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-ink py-24 md:py-32" data-testid="process-preview">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading dark eyebrow={t("process.eyebrow")} title={t("process.title")} />
            <Link to="/process" data-testid="process-view-link"
              className="group mb-1 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors hover:text-gold-soft">
              {t("nav.process")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 flex snap-x gap-5 overflow-x-auto pb-4 lg:grid lg:grid-cols-4 lg:overflow-visible">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.id} delay={(i % 4) * 0.08} className="min-w-[240px] snap-start">
              <div data-testid={`process-step-${step.id}`} className="relative h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <span className="font-display text-4xl text-gold/40">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-display text-xl text-offwhite">{step.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/55">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 max-w-2xl text-xs leading-relaxed text-white/40">{t("process.note")}</p>
      </div>
    </section>
  );
};

export const ProjectsPreview = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/projects")
      .then((r) => {
        const featured = r.data.filter((p) => p.featured);
        setProjects((featured.length ? featured : r.data).slice(0, 3));
      })
      .catch(() => {});
  }, []);

  return (
    <section className="bg-offwhite py-24 md:py-32" data-testid="projects-preview">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading eyebrow={t("projects.eyebrow")} title={t("projects.title")} />
            <Link to="/projects" data-testid="projects-view-all-link"
              className="group mb-1 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-gold-dark transition-colors hover:text-gold">
              {t("cta.viewAll")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        {projects.length > 0 ? (
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </div>
        ) : (
          <div className="mt-14 rounded-2xl border border-charcoal/10 bg-white p-12 text-center" data-testid="projects-empty">
            <p className="mx-auto max-w-md text-sm text-charcoal/60">{t("projects.empty")}</p>
            <ConsultButton testid="projects-empty-consult-button" className="mt-6" />
          </div>
        )}
      </div>
    </section>
  );
};
